import React, { useRef } from 'react'
import { Link } from 'react-router-dom'
import { motion, useInView } from 'framer-motion'
import { Link2, Smartphone, Share2, BarChart2, Shield, Zap, CreditCard } from 'lucide-react'
import Navbar from '../components/Common/Navbar'
import Footer from '../components/Common/Footer'

const B = 'https://pixner.net/bankio/main/assets/images'

const FadeUp = ({ children, delay = 0, className = '' }) => {
  const ref = useRef(null)
  const inView = useInView(ref, { once: true, margin: '-60px' })
  return (
    <motion.div ref={ref} className={className}
      initial={{ opacity: 0, y: 28 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.6, delay, ease: [0.22, 1, 0.36, 1] }}>
      {children}
    </motion.div>
  )
}

const features = [
  { icon: <Zap size={22} />, title: 'Create in seconds', desc: 'Generate a payment link or checkout page in under 60 seconds — no code, no technical setup required.' },
  { icon: <Share2 size={22} />, title: 'Share anywhere', desc: 'Share via WhatsApp, SMS, email, or embed as a button on your website or social media bio.' },
  { icon: <Smartphone size={22} />, title: 'Mobile optimized', desc: 'Beautiful, fast checkout experience on any device. Customers pay without leaving your message thread.' },
  { icon: <BarChart2 size={22} />, title: 'Real-time tracking', desc: 'See who clicked, who paid, and who didn\'t — and send payment reminders in one click.' },
  { icon: <Shield size={22} />, title: 'Bank-grade security', desc: 'Secure encrypted checkout. Customers trust the A1XPAY-secured badge on your payment page.' },
  { icon: <Link2 size={22} />, title: 'Custom branding', desc: 'Add your logo, brand colors, and a custom message to every payment link and checkout page.' },
]

export default function PaymentLinkCheckout() {
  return (
    <>
      <style>{css}</style>
      <div className="sv-root">
        <Navbar />

        {/* Hero */}
        <section className="sv-hero">
          <div className="sv-container sv-hero-grid">
            <motion.div className="sv-hero-inner"
              initial={{ opacity: 0, y: 32 }} animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.65, ease: [0.22, 1, 0.36, 1] }}>
              <span className="sv-sub">Payment Links & Checkout</span>
              <h1 className="sv-hero-h1">Accept payments without a website</h1>
              <p className="sv-hero-p">Create a payment link in 60 seconds. Share on WhatsApp, email, or Instagram. Get paid instantly — no coding, no merchant account setup.</p>
              <div className="sv-hero-btns">
                <Link to="/register" className="sv-btn-primary">Create free account</Link>
                <Link to="/pricing" className="sv-btn-ghost">See pricing</Link>
              </div>
              <div className="sv-trust-strip">
                {['No setup fees', 'UPI, Cards & Wallets', 'Instant settlement'].map(t => (
                  <span key={t} className="sv-trust-item"><div className="sv-trust-dot" />{t}</span>
                ))}
              </div>
            </motion.div>

            {/* Payment link mockup card */}
            <motion.div className="sv-mockup-wrap"
              initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.7, delay: 0.15 }}>
              <div className="sv-link-card">
                <div className="sv-link-header">
                  <div className="sv-link-icon-wrap"><Link2 size={14} /></div>
                  <span className="sv-link-title">Payment Link</span>
                  <span className="sv-link-live">● Live</span>
                </div>
                <div className="sv-link-url">pay.a1xpay.com/v/YourBusiness</div>
                <div className="sv-link-amount">₹5,000</div>
                <div className="sv-link-desc">Web Design — 50% Advance</div>
                <div className="sv-link-methods">
                  {['UPI', 'Cards', 'Net Banking', 'Wallets'].map(m => (
                    <span key={m} className="sv-method-badge">{m}</span>
                  ))}
                </div>
                <div className="sv-link-stats">3 views · 1 paid · ₹5,000 collected</div>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Features */}
        <section className="sv-section sv-bg-white">
          <div className="sv-container">
            <FadeUp><div className="sv-sec-head"><span className="sv-sub sv-green">Features</span><h2 className="sv-h2">Everything you need to collect payments</h2></div></FadeUp>
            <div className="sv-cards">
              {features.map((f, i) => (
                <FadeUp key={f.title} delay={i * 0.07}>
                  <div className="sv-card">
                    <div className="sv-card-icon">{f.icon}</div>
                    <h4 className="sv-card-title">{f.title}</h4>
                    <p className="sv-card-desc">{f.desc}</p>
                  </div>
                </FadeUp>
              ))}
            </div>
          </div>
        </section>

        {/* Who uses */}
        <section className="sv-section sv-bg-light">
          <div className="sv-container sv-feat-grid">
            <FadeUp className="sv-feat-text">
              <div className="sv-circle"><Link2 size={26} color="#fff" /></div>
              <span className="sv-sub sv-green">Who uses payment links</span>
              <h2 className="sv-h2">Perfect for any business that collects money</h2>
              <ul className="sv-list">
                {['Freelancers & consultants billing clients', 'Service businesses taking advance bookings', 'Events & registrations with instant confirmation', 'E-commerce businesses without a full website', 'Educational institutes & coaching centers', 'NGOs accepting online donations'].map(t => (
                  <li key={t} className="sv-li"><div className="sv-dot" /><span>{t}</span></li>
                ))}
              </ul>
            </FadeUp>
            <FadeUp delay={0.1} className="sv-img">
              <img src={`${B}/apps.png`} alt="Payment links"
                onError={e => { e.target.src = `${B}/feature-item-1.png` }} />
            </FadeUp>
          </div>
        </section>

        {/* Second feature — payment cards */}
        <section className="sv-section sv-bg-white">
          <div className="sv-container sv-feat-grid sv-feat-rev">
            <FadeUp delay={0.05} className="sv-img sv-cards-pair">
              <motion.img src={`${B}/card-img-1.png`} alt="Payment card"
                className="sv-card-img-1"
                whileHover={{ scale: 1.06, rotate: -3 }} transition={{ type: 'spring', stiffness: 200 }} />
              <motion.img src={`${B}/card-img-2.png`} alt="Payment card back"
                className="sv-card-img-2"
                whileHover={{ scale: 1.06, rotate: 3 }} transition={{ type: 'spring', stiffness: 200 }} />
            </FadeUp>
            <FadeUp className="sv-feat-text">
              <div className="sv-circle"><CreditCard size={26} color="#fff" /></div>
              <span className="sv-sub sv-green">Universal acceptance</span>
              <h2 className="sv-h2">Every card, every bank, every wallet</h2>
              <p className="sv-para">Your customers can pay using Visa, Mastercard, Rupay, any UPI app, net banking from 60+ banks, or popular wallets. No payment method left behind.</p>
              <ul className="sv-list">
                {['Visa, Mastercard, Rupay credit & debit cards', 'UPI apps: GPay, PhonePe, Paytm & 50+ more', 'Net banking — 60+ Indian banks supported', 'EMI on credit cards (6, 9, 12, 24 months)'].map(t => (
                  <li key={t} className="sv-li"><div className="sv-dot" /><span>{t}</span></li>
                ))}
              </ul>
            </FadeUp>
          </div>
        </section>

        <section className="sv-cta">
          <div className="sv-container sv-cta-flex">
            <FadeUp className="sv-cta-text">
              <span className="sv-sub sv-white">Start collecting payments today</span>
              <h2 className="sv-cta-h2">Create your first payment link in under 2 minutes</h2>
              <Link to="/register" className="sv-btn-white">Create free account</Link>
            </FadeUp>
            <motion.div className="sv-cta-illu"
              initial={{ opacity: 0, x: 40 }} whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }} transition={{ duration: 0.7, delay: 0.2 }}>
              <img src={`${B}/get-start.png`} alt="Get started" />
            </motion.div>
          </div>
        </section>
        <Footer />
      </div>
    </>
  )
}

const css = `
@import url('https://fonts.googleapis.com/css2?family=Jost:wght@300;400;500;600;700;800;900&display=swap');
.sv-root{font-family:'Jost',sans-serif;background:#fff;}
.sv-container{max-width:1200px;margin:0 auto;padding:0 1.5rem;}
@media(min-width:1200px){.sv-container{padding:0 2.5rem;}}
.sv-hero{background:#f5faff;padding:100px 0 80px;}
.sv-hero-grid{display:grid;grid-template-columns:1fr;gap:3rem;align-items:center;}
@media(min-width:1024px){.sv-hero-grid{grid-template-columns:6fr 5fr;}}
.sv-hero-inner{max-width:580px;}
.sv-sub{font-size:1rem;font-weight:600;color:#055C2D;margin-bottom:0.875rem;display:block;}
.sv-green{color:#055C2D;}
.sv-white{color:rgba(255,255,255,0.85);}
.sv-hero-h1{font-size:clamp(2rem,4.5vw,3.25rem);font-weight:800;color:#0A1023;letter-spacing:-0.04em;line-height:1.1;margin-bottom:1.25rem;}
.sv-hero-p{font-size:1.0625rem;color:#2C3655;line-height:1.75;max-width:500px;margin-bottom:2rem;}
.sv-hero-btns{display:flex;gap:1rem;flex-wrap:wrap;margin-bottom:1.5rem;}
.sv-trust-strip{display:flex;gap:1.25rem;flex-wrap:wrap;}
.sv-trust-item{display:flex;align-items:center;gap:0.5rem;font-size:0.875rem;color:#2C3655;font-weight:500;}
.sv-trust-dot{width:8px;height:8px;border-radius:50%;background:#055C2D;flex-shrink:0;}
.sv-mockup-wrap{display:none;}
@media(min-width:1024px){.sv-mockup-wrap{display:block;}}
.sv-link-card{background:#fff;border:1.5px solid #E8EBF4;border-radius:1.25rem;padding:1.75rem;box-shadow:0 8px 32px rgba(47,65,129,0.10);}
.sv-link-header{display:flex;align-items:center;gap:0.625rem;margin-bottom:1rem;}
.sv-link-icon-wrap{width:2rem;height:2rem;background:#EEF3FF;border-radius:0.4rem;display:flex;align-items:center;justify-content:center;color:#1A4DBE;}
.sv-link-title{flex:1;font-size:0.875rem;font-weight:700;color:#0A1023;}
.sv-link-live{font-size:0.75rem;font-weight:600;color:#055C2D;}
.sv-link-url{font-size:0.8125rem;color:#64748B;margin-bottom:1rem;font-family:monospace;}
.sv-link-amount{font-size:2.25rem;font-weight:800;color:#0A1023;letter-spacing:-0.04em;}
.sv-link-desc{font-size:0.875rem;color:#64748B;margin-bottom:1rem;}
.sv-link-methods{display:flex;gap:0.4rem;flex-wrap:wrap;margin-bottom:1rem;}
.sv-method-badge{padding:0.2rem 0.625rem;background:#f5faff;border:1px solid #D8DFEE;border-radius:999px;font-size:0.72rem;color:#2C3655;font-weight:500;}
.sv-link-stats{font-size:0.8rem;color:#64748B;}
.sv-section{padding:80px 0;}
.sv-bg-white{background:#fff;}
.sv-bg-light{background:#f5faff;}
.sv-feat-grid{display:grid;grid-template-columns:1fr;gap:3rem;align-items:center;}
@media(min-width:1024px){.sv-feat-grid{grid-template-columns:1fr 1fr;gap:5rem;}}
.sv-img{display:flex;align-items:center;justify-content:center;}
.sv-img img{max-width:100%;max-height:400px;object-fit:contain;}
.sv-circle{width:60px;height:60px;border-radius:50%;background:linear-gradient(135deg,#1A4DBE,#06B6D4);display:flex;align-items:center;justify-content:center;margin-bottom:1.5rem;box-shadow:0 8px 24px rgba(26,77,190,0.3);}
.sv-h2{font-size:clamp(1.75rem,3.5vw,2.5rem);font-weight:700;color:#0A1023;letter-spacing:-0.03em;line-height:1.2;margin-bottom:1rem;}
.sv-para{font-size:1rem;color:#2C3655;line-height:1.75;margin-bottom:1.5rem;}
.sv-list{list-style:none;padding:0;margin:0 0 1.75rem;display:flex;flex-direction:column;gap:0.75rem;}
.sv-li{display:flex;align-items:center;gap:0.625rem;}
.sv-dot{min-width:22px;min-height:22px;border-radius:50%;background:#055C2D;display:flex;align-items:center;justify-content:center;}
.sv-dot::after{content:'';width:8px;height:5px;border-left:2px solid #fff;border-bottom:2px solid #fff;transform:rotate(-45deg);display:block;margin-top:-2px;}
.sv-li span{font-size:1rem;color:#2C3655;font-weight:500;}
.sv-sec-head{text-align:center;margin-bottom:3rem;}
.sv-cards{display:grid;grid-template-columns:1fr;gap:1.25rem;}
@media(min-width:640px){.sv-cards{grid-template-columns:repeat(2,1fr);}}
@media(min-width:1024px){.sv-cards{grid-template-columns:repeat(3,1fr);}}
.sv-card{background:#f5faff;border:1.5px solid #E8EBF4;border-radius:1rem;padding:1.5rem;transition:border-color 0.2s,transform 0.2s;}
.sv-card:hover{border-color:#B0BAF1;transform:translateY(-3px);}
.sv-card-icon{width:2.5rem;height:2.5rem;background:#EEF3FF;border-radius:0.5rem;display:flex;align-items:center;justify-content:center;color:#1A4DBE;margin-bottom:0.875rem;}
.sv-card-title{font-size:1rem;font-weight:700;color:#0A1023;margin-bottom:0.375rem;}
.sv-card-desc{font-size:0.875rem;color:#2C3655;line-height:1.6;}
.sv-feat-rev .sv-img{order:0;}
@media(min-width:1024px){.sv-feat-rev .sv-img{order:-1;}}
.sv-feat-text{display:flex;flex-direction:column;}
.sv-cards-pair{position:relative;display:flex;justify-content:center;align-items:center;gap:-20px;}
.sv-card-img-1{max-width:260px;transform:rotate(-4deg);}
.sv-card-img-2{max-width:260px;transform:rotate(4deg);margin-left:-40px;}
.sv-cta{background:#1A4DBE;padding:80px 0;position:relative;overflow:hidden;}
.sv-cta::before{content:'';position:absolute;width:400px;height:400px;border-radius:50%;background:rgba(255,255,255,0.05);top:-150px;right:-80px;}
.sv-cta-flex{position:relative;z-index:2;display:flex;align-items:center;justify-content:space-between;gap:2rem;flex-wrap:wrap;}
.sv-cta-text{flex:1;min-width:280px;text-align:left;}
.sv-cta-illu{display:none;}
@media(min-width:900px){.sv-cta-illu{display:block;width:200px;flex-shrink:0;}}
.sv-cta-illu img{max-width:100%;filter:brightness(1.1) drop-shadow(0 8px 24px rgba(0,0,0,0.2));}
.sv-cta-h2{font-size:clamp(1.875rem,4vw,2.75rem);font-weight:800;color:#fff;letter-spacing:-0.04em;margin-bottom:2rem;}
.sv-btn-primary{display:inline-flex;align-items:center;padding:0.7rem 1.75rem;font-size:1rem;font-weight:600;color:#fff;background:#1A4DBE;border:1.5px solid #1A4DBE;border-radius:50px;text-decoration:none;transition:all 0.2s;font-family:'Jost',sans-serif;}
.sv-btn-primary:hover{background:#1540a8;transform:translateY(-1px);}
.sv-btn-ghost{display:inline-flex;align-items:center;padding:0.7rem 1.75rem;font-size:1rem;font-weight:600;color:#0A1023;background:transparent;border:1.5px solid #848894;border-radius:50px;text-decoration:none;transition:all 0.2s;font-family:'Jost',sans-serif;}
.sv-btn-ghost:hover{color:#1A4DBE;border-color:#1A4DBE;}
.sv-btn-white{display:inline-flex;align-items:center;padding:0.7rem 1.75rem;font-size:1rem;font-weight:600;color:#1A4DBE;background:#fff;border:1.5px solid #fff;border-radius:50px;text-decoration:none;transition:all 0.2s;font-family:'Jost',sans-serif;}
.sv-btn-white:hover{background:#f0f4ff;}
`
